import React, {useRef, useEffect, useState} from 'react'
import { DisplayRetailMediaSlotProps, RetailMediaResponse, RetailMediaBanner } from '../../../types/retail-media'
import { Stack, Box } from '../../../vanilla'
import { isServer, isMobile } from '../../../utils/utils'
import { DefaultContentBody } from 'dc-delivery-sdk-js'
import { getBannerSlot, getRowSlots } from './display-slots'
import { useSelectPromotionTracking } from '../../../analytics/promotion-tracking'
import { useIntersectionObserver } from '../../../hooks/use-intersection-observer'
import { sendRMImpressions, sendRMClicks } from '../../../analytics/retail-media-tracking'
import { BannerRowSkeleton } from '../../../components/skeletons'
import { useRetailMediaStore } from '../../../store/hooks/useStore'
import { Link } from '../../../components/link'

/**
 * Displays Retail Media content
 * @param { DisplayRetailMediaSlotProps } slots Slot Banner Content
 * @returns { ReactNode } to render within the PLP page
 */
export const DisplayRetailMediaSlot = ({slot, slotIndex, slotGroupName, campaignId}: DisplayRetailMediaSlotProps) => {

  const ref = useRef<HTMLDivElement | null>(null)
  const [isRMImpressionsSent, setisRMImpressionsSent] = useState(false)
  const retailMediaStore = useRetailMediaStore()

  // Add a 200px offset as a buffer to preload just before the slot enters the screen
  // Helps avoid visible loading & layout shifting
  const { isOnScreen: shouldPreload } = useIntersectionObserver(ref, {
    useOnce: false,
    rootMargin: '0px 0px 200px 0px'
  })

  const {isOnScreen: isTrueView} = useIntersectionObserver(ref, {
    useOnce: true,
    /* Offset for intersection track. Sometimes a user can view the top of the product list section on a PDP,
    but the actual banners are still not visible, so adding an offset to ensure that the event only fires when the user has scrolled to and viewed the product items,
      only then change observer state. */
    rootMargin: '-150px 0px -150px 0px',
  })
    
  // Send event to Retail Media when user scrolls to banner
  useEffect(() => {
      if ( isTrueView && slot?.retailMediaContent && !isRMImpressionsSent) {
        setisRMImpressionsSent(true)
        sendRMImpressions(slot?.id, retailMediaStore.retailMediaUrl)
    }
  }, [slot, isTrueView])

  const sendSelectPromotion = useSelectPromotionTracking()

  const [shouldDisplayContent, setShouldDisplayContent] = useState<boolean>(false)
  const [slotLoaded, setSlotLoaded] = useState<boolean>(false)

  // Creates the object required for the select_promotion event
  const selectPromotionInfo = {
    creative_name: slot?.text ?? 'Retail Media banner with no text field',
    creative_slot: `${slot?.slotId}_${slotGroupName}`,
    promotion_id: slot?.linkUrl ?? 'Retail Media banner with no linkUrl',
    promotion_name: "", // Not available on static banners - banner X implementation needed
    link_position: slot?.position ?? 1,
    slot_position: slotIndex,
    campaign_ID:campaignId
  }
  
  useEffect(() => {
    if (shouldPreload && slot && !shouldDisplayContent) {
      setShouldDisplayContent(true)
    }
  }, [shouldPreload, slot])

  const promotionElement = <Box 
      as='img'
      key={`${slot.id}-${slot.position}`}
      src={slot.imageUrl}
      alt={slot.altText}
      draggable={false}
      style={{ display: slotLoaded ? 'block' : 'none', width: '100%'}}
      data-test-selector="retail-media-slot-content"
      onClick={() => {
        sendSelectPromotion(selectPromotionInfo)
      }}
      onLoad={function() {
        setSlotLoaded(true)
      }}
    /> 

  return (
    <Stack 
      gap={['10px', '16px']}
      marginBottom={['10px', '16px']}
      ref={ref}
      onClick={()=> {sendRMClicks(slot.id, retailMediaStore.retailMediaUrl)}}
      key={`${slot.id}-${slot.position}`}
      data-test-selector="retail-media-slot-container"
    >
      {!slotLoaded && <BannerRowSkeleton />}
      {shouldDisplayContent && !slot.linkUrl && promotionElement}
      {shouldDisplayContent && slot.linkUrl &&
          <Link
            href={slot.linkUrl}
            key={`${slot.id}-${slot.position}`}
            style={{ display: slotLoaded ? 'block' : 'none'}}
            draggable={false}
            data-test-selector="retail-media-slot-link"
            disableExternalLinkIcon
          >
          {promotionElement}
        </Link>
      }
    </Stack>
  )
}

/**
 * Checks through Retail Media response and returns the correct banner for the correct slotId
 * @param { RetailMediaResponse } data response object back from Epsilon
 * @param { string } slotPosition position of required slots
 * @returns { ReactNode[] } an array of Retail Media slot banners valid for the correct screensize
 */
export const getRetailMediaSlot = (data: RetailMediaResponse, slotPosition: string) =>{
  if (!data || !slotPosition) return
  const isMobileFlag = isServer ? false : isMobile()
  const retailMediaSlots: RetailMediaBanner[] = []

  data?.banners &&
  data?.banners.length > 0 &&
  data?.banners?.map((banner: RetailMediaBanner, idx: number)=> {
    if (banner?.slotId.startsWith(`${slotPosition}-slot`)){
      retailMediaSlots.push({
        ...banner,
        retailMediaContent: data?.retailMediaContent
      })
    }
  })
  // Render the correct banner for the screensize
  const banners = 
    isMobileFlag ?
    retailMediaSlots.filter((banner) => isMobileFlag && banner.slotId === `${slotPosition}-slot-mobile`) :
    retailMediaSlots.filter((banner) => banner.slotId === `${slotPosition}-slot-web`)
  return banners
}

/**
 * Checks through Retail Media reponse and returns only valid top slot banners
 * @param { RetailMediaResponse } data response object back from Epsilon
 * @param { number[] } slotIndexes slotIndex from the Amplience data if available
 * @param { string[] } slotGroupNames slotGroupNames from the Amplience data if available
 * @returns {ReactNode[]} an array of Retail Media top slot banners valid for the correct screensize
 */
export const getRetailMediaTopSlot = (data: RetailMediaResponse, slotIndexes?: number[], slotGroupNames?: string[], campaignId?: string) =>{
  const banners = getRetailMediaSlot(data, 'Above-Grid')
  if (!banners) return
  return banners.map((banner, idx) => {
    const slotIndex = slotIndexes ? slotIndexes[idx] : undefined
    const slotGroupName = slotGroupNames ? slotGroupNames[idx] : undefined
    return <DisplayRetailMediaSlot slot={banner} slotIndex={slotIndex} slotGroupName={slotGroupName || banner.slotGroupName} campaignId={campaignId}/>
  })
}

/**
 * Sets the correct top slots for the PLP page
 * @param { DefaultContentBody } pageData the page from the content store
 * @param { RetailMediaResponse } retailMediaData an array of banner data from the retail media call
 * @returns {ReactNode[]} an array of ReactNodes from Amplience & Retail Media to display on the FE
 */
export const setTopSlot = (pageData: DefaultContentBody, retailMediaData: RetailMediaResponse) => {
  const { topSlot } = getBannerSlot(pageData)
  
  const topSlotFiltered = (topSlot || []).filter(Boolean)
  if (!topSlotFiltered.length || !retailMediaData || !retailMediaData.banners || !retailMediaData.banners.length) {
    return { topSlot: topSlotFiltered }
  }

  // Loop through all filtered topSlots and create an array of slotIndexes and slotGroupNames for the Retail Media banners
  const slotIndexes: number[] = topSlotFiltered.filter(slot => slot.props.slots.enableRetailMedia).map(slot => slot.props.slotIndex)
  const slotGroupNames: string[] = topSlotFiltered.filter(slot => slot.props.slots.enableRetailMedia).map(slot => slot.props.slotGroupName)
  const campaignId = topSlotFiltered[0].props?.slots?.default?.[0]?.campaign?.[0]?.campaignId || ''
  const retailMediaBanners = getRetailMediaTopSlot(retailMediaData, slotIndexes, slotGroupNames, campaignId)

  if (retailMediaBanners && retailMediaBanners.length > 0) {
    for (let i = 0; i < topSlotFiltered.length; i++) {
      // If the Amplience slot has enableRetailMedia flag and there is a valid banner in the RM response, replace the Amplience banner
      if (topSlotFiltered[i].props.slots.enableRetailMedia) {
        const retailMediaBanner = retailMediaBanners.shift()
        if (retailMediaBanner) {
          const updatedRetailMediaBanner = {...retailMediaBanner,  props:{...retailMediaBanner.props, campaignId: campaignId}}
          topSlotFiltered.splice(i, 1, updatedRetailMediaBanner)
        }
      }
    }
  }
  return { topSlot: topSlotFiltered }
}

/**
 * Sets the correct row slots for the PLP page
 * @param { DefaultContentBody } pageData the page from the content store
 * @param { RetailMediaResponse } retailMediaData an array of banner data from the retail media call
 * @returns { ReactNode[] } an array of ReactNodes from Amplience & Retail Media to display on the FE
 */
export const setRowSlots = (pageData: DefaultContentBody, retailMediaData: RetailMediaResponse) => {
  const rowSlots = getRowSlots(pageData) || []  
  if (!rowSlots.length || !retailMediaData || !retailMediaData.banners || !retailMediaData.banners.length) {
    return rowSlots
  }

  const retailMediaBanners = getRetailMediaSlot(retailMediaData, 'Grid')
  if (retailMediaBanners && retailMediaBanners.length > 0) {
    for (let i = 0; i < rowSlots.length; i++) {
      // If the Amplience slot has enableRetailMedia flag and there is a valid banner in the RM response, replace the Amplience banner
      if (rowSlots[i].enableRetailMedia) {
        const retailMediaBanner = retailMediaBanners.shift()
        if (retailMediaBanner) {
          const campaignId =rowSlots?.[i]?.default?.[0]?.campaign?.[0]?.campaignId || ''
          rowSlots[i] = {...retailMediaBanner, slotPosition: rowSlots[i].originalSlotIndex, slotGroupName: rowSlots[i]._meta?.name, campaignId: campaignId}
        }
      }
    }
  }
  return rowSlots
}